import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { StatusBadge } from "../components/ProductCard";
import { Activity, RefreshCw } from "lucide-react";

const StatusSummaryCard = ({ label, count, color, borderColor }) => (
  <div className={`bg-[#0a0a12] border ${borderColor} p-6`}>
    <div className="flex items-center gap-3 mb-2">
      <span className={`w-3 h-3 rounded-full ${color} status-pulse`} />
      <span className={`font-heading uppercase`}>{label}</span>
    </div>
    <span className="text-4xl font-mono text-white">{count}</span>
  </div>
);

const StatusTable = ({ game, products }) => {
  const safeProducts = Array.isArray(products) ? products : [];

  return (
    <div className="bg-[#0a0a12] border border-white/5 overflow-hidden">
      <div className="bg-[#08080e] px-6 py-4 border-b border-white/5">
        <h2 className="font-heading text-lg text-white">{game}</h2>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/5">
            <th className="text-left text-[#A1A1AA] text-sm font-medium p-4 uppercase tracking-wider">Product</th>
            <th className="text-right text-[#A1A1AA] text-sm font-medium p-4 uppercase tracking-wider">Status</th>
          </tr>
        </thead>
        <tbody>
          {safeProducts.map((product) => (
            <tr key={product.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
              <td className="p-4">
                <a href={`/products/${product.id}`} className="text-white hover:text-[#4F7CFF] transition-colors">
                  {product.name}
                </a>
              </td>
              <td className="p-4 text-right">
                <StatusBadge status={product.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const StatusPage = () => {
  const [statusData, setStatusData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      if (!API) {
        setStatusData([]);
        return;
      }

      const response = await axios.get(`${API}/status-overview`).catch(() => ({ data: [] }));
      setStatusData(Array.isArray(response.data) ? response.data : []);
      setLastUpdated(new Date());
    } catch {
      setStatusData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const safeStatusData = Array.isArray(statusData) ? statusData : [];

  const statusCounts = {
    undetected: safeStatusData.filter(s => s.status === "undetected").length,
    updating: safeStatusData.filter(s => s.status === "updating").length,
    detected: safeStatusData.filter(s => s.status === "detected").length
  };

  const groupedByGame = safeStatusData.reduce((acc, item) => {
    if (!item?.game) return acc;
    if (!acc[item.game]) acc[item.game] = [];
    acc[item.game].push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#050508]">
      <Navbar />

      <main className="pt-24 pb-20 relative">
        <div className="absolute inset-0 scanlines opacity-30 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl text-white flex items-center gap-3 mb-2">
                <Activity className="w-8 h-8 text-[#4F7CFF]" />
                System Status
              </h1>
              <p className="text-[#A1A1AA]">Real-time status of all products</p>
            </div>

            <div className="mt-4 md:mt-0 flex items-center gap-4">
              <span className="text-[#A1A1AA] text-sm font-mono">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
              <button
                onClick={fetchStatus}
                disabled={loading}
                className="btn-secondary px-4 py-2 flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            <StatusSummaryCard label="Undetected" count={statusCounts.undetected} color="bg-[#00FF66]" borderColor="border-[#00FF66]/30" />
            <StatusSummaryCard label="Updating" count={statusCounts.updating} color="bg-[#F5A623]" borderColor="border-[#F5A623]/30" />
            <StatusSummaryCard label="Detected" count={statusCounts.detected} color="bg-[#FF3B30]" borderColor="border-[#FF3B30]/30" />
          </div>

          {loading && safeStatusData.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#4F7CFF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedByGame).map(([game, products]) => (
                <StatusTable key={game} game={game} products={products} />
              ))}
            </div>
          )}

          <div className="mt-10 bg-[#0a0a12] border border-white/5 p-6">
            <h3 className="font-heading text-lg text-white mb-4">Status Legend</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <span className="w-3 h-3 rounded-full bg-[#00FF66] mt-1" />
                <div>
                  <p className="text-white font-medium">Undetected</p>
                  <p className="text-[#A1A1AA] text-sm">Fully operational.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-3 h-3 rounded-full bg-[#F5A623] mt-1" />
                <div>
                  <p className="text-white font-medium">Updating</p>
                  <p className="text-[#A1A1AA] text-sm">Temporarily updating.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-3 h-3 rounded-full bg-[#FF3B30] mt-1" />
                <div>
                  <p className="text-white font-medium">Detected</p>
                  <p className="text-[#A1A1AA] text-sm">Not recommended.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StatusPage;
